/**
 * 組織レポート（社長向け / COMPANY_WIDE）
 *
 * 方針（フェーズ1）:
 *  - 会社選択UIを持たず、ログインユーザーの自社を対象に固定する。
 *  - 開いた時点で本文を「HTML」として画面内に表示する（PDF化はしない）。
 *  - 右上の「PDFとして保存する」を押したときに初めて A4横PDF を生成・保存する。
 *
 * レポート本文（HTML）とPDF化は既存資産をそのまま利用する:
 *  - generateTeamReportHtml() … A4横1ページ = .c-report-single_list_item
 *  - generateTeamReportPdf()  … 上記ページ単位で modern-screenshot + jsPDF
 *
 * ※ 管理者向けの /aggregation/company/network（会社選択・部署絞り込み・ネットワーク調整）と
 *   モバイルWebViewが読む /embed/report/org（OrgReportRealPage）は変更していない。
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Button, Typography, Alert, CircularProgress } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useAuth } from '../../auth/context/AuthContext';
import { analysisApi } from '../../analysis/api/analysisApi';
import { organizationApi } from '../../admin/api/organizationApi';
import { generateTeamReportHtml, type MemberScore } from '../utils/teamReportGenerator';
import {
  buildOrgReportFromResults,
  type OrgMemberResult,
} from '../../analysis/utils/buildOrgReportFromResults';
import { unwrapGb74Scores } from '../../analysis/utils/gb74Report';
import { classifyOrgType } from '../../analysis/utils/orgTypeClassifier';
import { generateTeamReportPdf } from '../utils/pdfGenerator';

/**
 * 画面とPDFファイル名に出す版数。
 * 「修正したのに変わらない」がコード側かブラウザキャッシュ側かを切り分けるための目印。
 * 修正を配信するたびに更新する。切り分けが済んだら表示は外してよい。
 */
const REPORT_BUILD = 'b7';

/** レポート本文（HTML）とPDFファイル名に使う日付文字列。 */
const formatDate = (d: Date): string =>
  d
    .toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })
    .replace(/\//g, '.');

export const OrgReportPdfPage = () => {
  const { user } = useAuth();
  const companyId = user?.companyId ?? '';
  const companyName = user?.companyName ?? '組織レポート';

  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const dateStr = useMemo(() => formatDate(new Date()), []);

  // 自社の分析結果からレポートHTMLを組み立てて画面に表示する。
  useEffect(() => {
    let aborted = false;
    const load = async () => {
      if (!companyId) {
        setError('会社情報が取得できませんでした。再ログインしてお試しください。');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);

        const response = await analysisApi.getCompanyResults(companyId);
        if (!response.results || response.results.length === 0) {
          if (!aborted) setError('分析結果がまだありません。社員の回答が完了すると表示されます。');
          return;
        }

        // スコアは gb74 形式の thinkingType(A/B/C/D) を使う。
        // レーダー(calculateRadarPath)は 0〜12 スケール前提で、thinkingType はそのまま
        // 0〜12 の個数なので変換せずに渡す（ここを誤ると全員0になり線が中心に潰れる）。
        const members: MemberScore[] = [];
        for (const r of response.results) {
          const gb74 = unwrapGb74Scores((r as { scores?: unknown }).scores);
          if (!gb74) continue;
          const tt = gb74.thinkingType ?? {};
          members.push({
            name: (r as { userName?: string; userId?: string }).userName || r.userId,
            scores: {
              A: Number(tt.A) || 0,
              B: Number(tt.B) || 0,
              C: Number(tt.C) || 0,
              D: Number(tt.D) || 0,
            },
          });
        }
        if (members.length === 0) {
          if (!aborted) setError('分析結果がまだありません。社員の回答が完了すると表示されます。');
          return;
        }

        // 部署別「つながりの構造」ページ用に、部署ごとの分析済みメンバーを組み立てる。
        // 部署情報の取得に失敗しても全社レポートは出せるよう best-effort（失敗時は部署ページなし）。
        let departments: Array<{ name: string; members: MemberScore[] }> = [];
        try {
          const deptList = await organizationApi.getDepartmentsByCompany(companyId);
          const byUserId = new Map<string, MemberScore>();
          response.results.forEach((r) => {
            const m = members.find(
              (x) => x.name === ((r as { userName?: string }).userName || r.userId)
            );
            if (m) byUserId.set(r.userId, m);
          });
          departments = (
            await Promise.all(
              deptList.map(async (d) => {
                const employees = await organizationApi.getEmployeesByDepartment(d.id);
                const deptMembers = employees
                  .map((e) => byUserId.get(e.id))
                  .filter((m): m is MemberScore => Boolean(m));
                return { name: d.name, members: deptMembers };
              })
            )
          ).sort((a, b) => b.members.length - a.members.length);
        } catch {
          departments = [];
        }

        // 組織スコア/タイプは Web版組織レポートと同じロジック（orgScore.ts）で算出し、
        // PDF と Web で数値が一致するようにする。gb74 形式でない結果は対象外。
        let orgEvaluation;
        let orgConnectionLead: string | undefined;
        try {
          const orgMembers: OrgMemberResult[] = [];
          for (const r of response.results) {
            const gb74 = unwrapGb74Scores((r as { scores?: unknown }).scores);
            if (!gb74) continue;
            orgMembers.push({
              scores: gb74,
              departmentName: (r as { departmentName?: string | null }).departmentName ?? null,
            });
          }
          if (orgMembers.length > 0) {
            const orgType = classifyOrgType(orgMembers);
            const org = buildOrgReportFromResults(orgMembers, {
              companyName,
              meta: `${orgMembers.length}名の回答に基づく集計`,
              applyHerrmannLoad: true,
            });
            orgConnectionLead = org.connectionLead;
            orgEvaluation = {
              totalScore: org.totalScore,
              totalGrade: org.totalGrade,
              evaluationLead: org.evaluationLead,
              gauges: org.gauges.map((g) => ({ label: g.label, score: g.score, grade: g.grade })),
              // 表示するタイプ名は12原型（GB組織タイプ12分類）を優先。
              // 未判定のときのみ従来の10分類ラベルへフォールバックする。
              orgTypeCode: org.orgArchetypeId ?? orgType.code,
              orgTypeName: org.orgArchetypeName
                ? `${org.orgArchetypeName}（${org.orgArchetypeCharacter}）`
                : orgType.label,
              orgTypeDesc: org.axisNote,
              // 組織ダイヤモンド（4軸平均・0〜12）
              axis: org.axis,
              axisNote: org.axisNote,
              // 社員の分布（0〜1 の正規化座標）
              // axis も渡す（散布図で各人のレーダー形を描くため）
              scatter: org.scatter?.map((d) => ({ x: d.x, y: d.y, axis: d.axis })),
              distributionLead: org.distributionLead,
              // 評価を上げるために
              improvementLead: org.improvementLead,
              improvementTarget: org.improvementTarget,
              improvements: org.improvements,
            };
          }
        } catch {
          orgEvaluation = undefined; // 算出できなくてもレポート自体は表示する
        }

        // 「つながりの構造」は Web版§03 と同じ相談ネットワークを使う。
        // 未整備・取得失敗時は undefined のままとし、旧・思考タイプ類似度の図にフォールバックする。
        let consultDepts;
        // 「つながりの構造とは」ページに載せる全社リード文（12原型の確定文言）
        let connectionLead: string | undefined;
        try {
          const net = await analysisApi.getCompanyConsultNetwork();
          if (net.consultNetwork?.depts?.length) {
            consultDepts = net.consultNetwork.depts;
          }
        } catch {
          consultDepts = undefined;
        }

        // 原型が判定できていれば、その「つながりリード」を解説ページに載せる
        if (orgConnectionLead) connectionLead = orgConnectionLead;

        const built = await generateTeamReportHtml({
          companyName,
          date: dateStr,
          members,
          departments,
          orgEvaluation,
          consultDepts,
          connectionLead,
        });
        if (!aborted) setHtml(built);
      } catch (e) {
        if (!aborted) {
          setError(e instanceof Error && e.message ? e.message : 'レポートの生成に失敗しました');
        }
      } finally {
        if (!aborted) setLoading(false);
      }
    };
    void load();
    return () => {
      aborted = true;
    };
  }, [companyId, companyName, dateStr]);

  // 表示中のHTMLをそのままA4横PDFにして保存する（押されたときだけ実行）。
  const handleSavePdf = useCallback(async () => {
    if (!html) return;
    try {
      setSaving(true);
      setError(null);
      await generateTeamReportPdf(html, `${dateStr} 組織レポート_${REPORT_BUILD}.pdf`);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'PDFの生成に失敗しました');
    } finally {
      setSaving(false);
    }
  }, [html, dateStr]);

  return (
    <Box>
      {/* ヘッダー: 右上に「PDFとして保存する」 */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h5">組織レポート</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            表示版 {REPORT_BUILD}
          </Typography>
          <Button
            variant="contained"
            startIcon={
              saving ? <CircularProgress size={18} color="inherit" /> : <PictureAsPdfIcon />
            }
            onClick={handleSavePdf}
            disabled={!html || saving || loading}
          >
            {saving ? 'PDFを作成中…' : 'PDFとして保存する'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity={html ? 'error' : 'info'} sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={360}>
          <CircularProgress />
        </Box>
      )}

      {/* 本文: レポートHTMLをそのまま表示する。iframe にすることで
          レポート側CSSと管理画面のスタイルが互いに影響しないようにする。 */}
      {!loading && html && (
        <Box
          sx={{
            border: '1px solid #E1E5EC',
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: '#fff',
          }}
        >
          <iframe
            title="組織レポート"
            srcDoc={html}
            style={{ width: '100%', height: '80vh', border: 'none', display: 'block' }}
          />
        </Box>
      )}
    </Box>
  );
};

export default OrgReportPdfPage;
